import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useConfig } from '../../contexts/ConfigContext';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../BrandLogo';

export default function MarketingNav() {
  const { config } = useConfig();
  const { user, signOut } = useAuth();
  const allowLogin = config?.enable_login !== false;
  const allowSelfSignup = config?.enable_self_signup === true;
  const allowPilot = config?.enable_pilot_requests !== false;
  const [scrolled, setScrolled] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      window.location.assign('/');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#071120]/85 border-b border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.35)]'
          : 'backdrop-blur-md bg-[#071120]/40 border-b border-transparent'
      }`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between transition-all ${scrolled ? 'py-3' : 'py-4'}`}>
        <Link to="/" className="group" data-testid="brand-link" aria-label="ComplyTradeIQ home">
          <BrandLogo
            variant="nav"
            className="transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          <a href="#workflow" className="hover:text-white transition-colors" data-testid="nav-workflow">Workflow</a>
          <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
          <a href="#use-cases" className="hover:text-white transition-colors" data-testid="nav-usecases">Use cases</a>
          <a href="#security" className="hover:text-white transition-colors" data-testid="nav-security">Security</a>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-dashboard-link">
                Dashboard
              </Link>
              <button onClick={onSignOut}
                disabled={signingOut}
                className="text-sm px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors"
                data-testid="nav-signout-btn">
                {signingOut ? 'Signing out...' : 'Sign out'}
              </button>
            </>
          ) : allowSelfSignup ? (
            <>
              {allowLogin && <Link to="/login" className="hidden sm:inline-block text-sm px-4 py-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-login-btn">Log in</Link>}
              <Link to="/signup" className="text-sm px-4 py-2 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover hover:scale-[1.03] active:scale-[0.99] transition-all" data-testid="nav-signup-btn">Start Validation</Link>
            </>
          ) : (
            <>
              {allowLogin && <Link to="/login" className="hidden sm:inline-block text-sm px-3 py-2 text-slate-400 hover:text-white transition-colors" data-testid="nav-login-btn">Log in</Link>}
              {allowPilot && (
                <Link to="/request-access"
                  className="text-sm px-4 py-2 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover hover:scale-[1.03] active:scale-[0.99] transition-all"
                  data-testid="nav-request-access-btn">
                  Request access
                </Link>
              )}
              <span className="hidden md:inline-flex text-[10px] text-slate-400 px-2 py-1 rounded-full border border-white/10 font-mono uppercase tracking-widest" data-testid="private-mode-badge">
                Private pilot
              </span>
            </>
          )}
        </div>
      </div>
    </motion.header>
  );
}
